import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { useCart, type CartItem } from "@/lib/cart";
import CartDrawer from "@/components/shop/CartDrawer";
import { Blue2Page, Part } from "../Page";
import { Draw, EASE, HEART, PAGES } from "../bits";
import "./Shop.css";

/* blue-2 shop: the same /api/products data, cart (lib/cart) and checkout
   (CartDrawer → /api/checkout) as the current /shop, restyled. Product links
   stay inside blue-2 at /blue-2/shop/:slug. */

export type ShopProduct = {
  id: string; slug: string; name: string; brand: string; description: string;
  featured: boolean; price: number; inventory: number; imageUrl: string | null; categoryId: string;
};
type Category = { id: string; slug: string; name: string };
type ShopResp = { products: ShopProduct[]; categories: Category[] };

export const money = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);

export const productHref = (slug: string) => `${PAGES.shop}/${slug}`;

/* ─── Cart access ─── */

/* The old navbar owned the cart drawer; blue-2's nav doesn't, so shop pages
   carry their own: a cart button in the header, a floating pill once the cart
   has something in it, and the same CartDrawer (and checkout) as /shop. */
const CartUI = createContext<{ open: () => void }>({ open: () => {} });
export const useCartUI = () => useContext(CartUI);

export function ShopCart({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const { totalItems } = useCart();
  return <CartUI.Provider value={{ open: () => setOpen(true) }}>
    {children}
    <AnimatePresence>
      {totalItems > 0 && !isOpen && <motion.button type="button" className="bs-float" onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.35, ease: EASE }}
        aria-label={`Open cart, ${totalItems} item${totalItems === 1 ? "" : "s"}`}>
        <ShoppingBag size={18} aria-hidden="true" /> Cart <b>{totalItems}</b>
      </motion.button>}
    </AnimatePresence>
    <CartDrawer open={isOpen} onClose={() => setOpen(false)} />
  </CartUI.Provider>;
}

export function CartButton() {
  const { open } = useCartUI();
  const { totalItems } = useCart();
  return <button type="button" className="bs-cart-btn" onClick={open} aria-label={`Open cart${totalItems ? `, ${totalItems} item${totalItems === 1 ? "" : "s"}` : ""}`}>
    <ShoppingBag size={18} aria-hidden="true" /> Your cart
    {totalItems > 0 && <b>{totalItems}</b>}
  </button>;
}

/** Same behavior as the shop's AddToCartButton (addItem, a 1.5 s "Added"),
    styled for blue-2, plus a quick way into the cart once something's in. */
export function AddButton({ item, outOfStock, big = false }: { item: Omit<CartItem, "quantity">; outOfStock: boolean; big?: boolean }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const timer = useRef<number>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  if (outOfStock) return <button type="button" className={`bs-add is-out${big ? " is-big" : ""}`} disabled>Sold out</button>;
  return <button type="button" className={`bs-add${added ? " is-added" : ""}${big ? " is-big" : ""}`} aria-live="polite"
    onClick={() => { addItem(item); setAdded(true); window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setAdded(false), 1500); }}>
    {added ? <><Check size={17} aria-hidden="true" /> Added!</> : <><ShoppingBag size={17} aria-hidden="true" /> Add to cart</>}
  </button>;
}

/* ─── Bits ─── */

/** The product photo, or a gingham swatch with a sewn-in label when there isn't one. */
export function ProductPicture({ src, name, brand, alt }: { src: string | null; name: string; brand: string; alt?: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="bs-pic bs-pic-blank" role="img" aria-label={`${name} (photo coming soon)`}>
    <span className="bs-label"><small>{brand}</small><b>{name}</b></span>
  </span>;
  return <span className="bs-pic"><img src={src} alt={alt ?? name} loading="lazy" onError={() => setFailed(true)} /></span>;
}

/** A stitched price tag. */
export function PriceTag({ cents, big = false }: { cents: number; big?: boolean }) {
  return <span className={`bs-price${big ? " is-big" : ""}`}><i aria-hidden="true" />{money(cents)}</span>;
}

export function StockNote({ inventory }: { inventory: number }) {
  if (inventory <= 0) return <p className="bs-stock is-out">sold out for now</p>;
  if (inventory <= 5) return <p className="bs-stock">only {inventory} left!</p>;
  return null;
}

const TILT = [-1.4, 1.1, -0.7, 1.6];
const TAPE = ["soft", "pop", "extra"] as const;

export function ProductCard({ p, i, feature = false }: { p: ShopProduct; i: number; feature?: boolean }) {
  const item = { productId: p.id, slug: p.slug, name: p.name, brand: p.brand, price: p.price, imageUrl: p.imageUrl };
  return <motion.li className={`bs-card${feature ? " is-feature" : ""}`} style={{ rotate: feature ? "0deg" : `${TILT[i % TILT.length]}deg` }}
    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.45, delay: Math.min(i, 8) * 0.05, ease: EASE }} layout>
    <Link to={productHref(p.slug)} className="bs-card-pic" aria-label={p.name}>
      <i className={`bs-tape bs-tape-${TAPE[i % 3]}`} aria-hidden="true" />
      <ProductPicture src={p.imageUrl} name={p.name} brand={p.brand} alt="" />
      {p.featured && <span className="bs-pick" aria-hidden="true">Chelsea’s<br />pick!</span>}
    </Link>
    <div className="bs-card-body">
      <span className="bs-brand">{p.brand}</span>
      <h3><Link to={productHref(p.slug)} className="bs-card-title">{p.name}</Link></h3>
      {p.description && <p className="bs-card-note">{p.description}</p>}
      <StockNote inventory={p.inventory} />
      <div className="bs-card-foot">
        <PriceTag cents={p.price} big={feature} />
        <AddButton item={item} outOfStock={p.inventory <= 0} big={feature} />
      </div>
      {feature && <Link to={productHref(p.slug)} className="bs-more">See the details <ArrowRight size={15} aria-hidden="true" /></Link>}
    </div>
  </motion.li>;
}

/* ─── Page ─── */

function Header() {
  return <div className="bs-head">
    <h1 className="bs-title">
      <motion.span className="bs-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE }}>a few things for your creative life</motion.span>
      <span className="bs-title-word" aria-label="shop">
        {"shop".split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
      </span>
    </h1>
    <svg className="bs-head-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d={HEART} delay={0.8} width={3.4} /></svg>
    <motion.div className="bs-head-side" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5, ease: EASE }}>
      <p className="bs-head-note">Shop A Few Loose Threads goods. Looking for my favorite sewing tools and supplies? <Link to={PAGES.favorites}>Find them in My Favorites.</Link></p>
      <CartButton />
    </motion.div>
  </div>;
}

export function useProducts() {
  const [data, setData] = useState<ShopResp | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get<ShopResp>("/api/products").then(setData).catch(() => setError(true)); }, []);
  return { data, error };
}

const ALL = "all";

export function Blue2Shop() {
  const { data, error } = useProducts();
  const [cat, setCat] = useState(ALL);
  const cats = useMemo(() => (data?.categories ?? []).filter(c => data?.products.some(p => p.categoryId === c.id)), [data]);
  const shown = useMemo(() => (data?.products ?? []).filter(p => cat === ALL || p.categoryId === cat), [data, cat]);
  // With just a couple of goods, each gets a big spread instead of a lonely grid tile.
  const feature = shown.length <= 2;

  return <ShopCart>
    <Blue2Page current="shop" tone="blush" header={<Header />}>
      <Part p="faves">
        <section className="b2-sec bs" aria-labelledby="bs-list-title">
          <div className="b2-wrap">
            <h2 id="bs-list-title" className="cb-sr">Everything in the shop</h2>
            {error && <p className="bs-state" role="alert">The shop didn’t load. Try again in a minute? <span className="bs-state-hand">(it’s not you, it’s the internet)</span></p>}
            {!error && !data && <ul className="bs-grid" aria-label="Loading the shop">{Array.from({ length: 3 }, (_, i) => <li key={i} className="bs-card bs-skel" aria-hidden="true" />)}</ul>}
            {data && <>
              {cats.length > 1 && <div className="bs-filters" role="group" aria-label="Filter the shop">
                <button type="button" aria-pressed={cat === ALL} onClick={() => setCat(ALL)}>Everything<small>{data.products.length}</small></button>
                {cats.map(c => <button key={c.id} type="button" aria-pressed={cat === c.id} onClick={() => setCat(c.id)}>{c.name}<small>{data.products.filter(p => p.categoryId === c.id).length}</small></button>)}
              </div>}
              {shown.length > 0
                ? <ul className={feature ? "bs-features" : "bs-grid"}><AnimatePresence mode="popLayout">{shown.map((p, i) => <ProductCard key={p.id} p={p} i={i} feature={feature} />)}</AnimatePresence></ul>
                : <p className="bs-state">Nothing in the shop right now. <span className="bs-state-hand">check back soon ♡</span></p>}
            </>}
          </div>
        </section>
      </Part>

      <Part p="loop">
        <section className="b2-sec bs-nudge" aria-labelledby="bs-nudge-title">
          <div className="b2-wrap bs-nudge-inner">
            <div>
              <p className="b2-note bs-nudge-hand">tools &amp; supplies?</p>
              <h2 id="bs-nudge-title" className="bs-nudge-title">My favorites live over here.</h2>
              <p className="bs-nudge-copy">The tools, machines, and fabric shops I actually use, with my discount codes.</p>
            </div>
            <Link to={PAGES.favorites} className="b2-btn b2-btn-blue bs-nudge-btn">See my favorites <ArrowRight size={18} aria-hidden="true" /></Link>
          </div>
        </section>
      </Part>
    </Blue2Page>
  </ShopCart>;
}
